import type { Table } from "@tanstack/react-table";
import { parseAsString, useQueryStates } from "nuqs";
import { useCallback, useEffect } from "react";

import type { TeamMemberRow } from "../components/team-members-table.js";
import { DEFAULT_PAGE } from "../constants.js";

// Hook to manage page state independently of the table
export const usePageState = () => {
  const [{ membersPage }, setMembersQuery] = useQueryStates({
    membersPage: parseAsString.withDefault("1"),
  });

  const parsedPage = Number(membersPage);
  const page =
    Number.isFinite(parsedPage) && parsedPage > 0 ? parsedPage : DEFAULT_PAGE;

  const setPage = useCallback(
    (newPage: number) => {
      setMembersQuery({ membersPage: String(newPage) });
    },
    [setMembersQuery]
  );

  const resetPagination = useCallback(() => {
    setMembersQuery({ membersPage: String(DEFAULT_PAGE) });
  }, [setMembersQuery]);

  return {
    page,
    pageIndex: Math.max(page - 1, 0),
    resetPagination,
    setPage,
  };
};

// Hook to add table-dependent pagination controls and validation
export const useTeamMembersPagination = (
  table: Table<TeamMemberRow>,
  pageState: ReturnType<typeof usePageState>
) => {
  const { page, setPage } = pageState;
  const pageCount = table.getPageCount();

  useEffect(() => {
    // Reset to page 1 when there's no data, or clamp to last page when out of bounds
    let targetPage = page;

    if (pageCount === 0) {
      targetPage = DEFAULT_PAGE;
    } else if (page > pageCount) {
      targetPage = pageCount;
    }

    if (targetPage !== page) {
      setPage(targetPage);
    }
  }, [page, pageCount, setPage]);

  return {
    ...pageState,
    hasNextPage: table.getCanNextPage(),
    hasPreviousPage: table.getCanPreviousPage(),
  };
};
